import React, { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import highlightMatch from "../utils/highlightMatch";
import { groupResults } from "../utils/searchMeta";

// Reusable grouped result list for the search dropdowns (desktop panel + mobile).
// Renders quiet group headers with an icon + count, then compact rows: a branded
// icon tile, the highlighted title/snippet, and an arrow on the active/hovered row.
//
// Props:
//   results     — flat, relevance-sorted result objects
//   query       — current query (for match highlighting)
//   activeIndex — keyboard-highlighted item index across the flattened list (-1 = none)
//   onSelect    — (url) => void, called when a row is chosen
//   onHover     — (index) => void, syncs the keyboard cursor to the mouse (optional)
export default function SearchDropdownResults({
  results,
  query,
  activeIndex = -1,
  onSelect,
  onHover,
}) {
  const groups = useMemo(() => groupResults(results), [results]);

  // Flatten to a linear row model so each item has a stable global index that
  // matches the visual order (used by the keyboard cursor).
  const rows = useMemo(() => {
    const out = [];
    let idx = 0;
    for (const g of groups) {
      out.push({ kind: "header", key: `h-${g.type}`, label: g.label, Icon: g.Icon, count: g.items.length });
      for (const item of g.items) {
        out.push({ kind: "item", key: `${item.url}-${idx}`, item, Icon: g.Icon, index: idx });
        idx++;
      }
    }
    return out;
  }, [groups]);

  return (
    <div className="py-1.5">
      {rows.map((row) =>
        row.kind === "header" ? (
          <div
            key={row.key}
            className="flex items-center gap-2 px-4 pt-3 pb-1.5"
          >
            <span className="text-[11px] font-[700] uppercase tracking-[0.08em] text-gray-400">
              {row.label}
            </span>
            <span className="text-[11px] font-[600] text-gray-300">
              {row.count}
            </span>
            <span className="flex-1 h-px bg-gray-100" />
          </div>
        ) : (
          <button
            key={row.key}
            onClick={() => onSelect && onSelect(row.item.url)}
            onMouseEnter={() => onHover && onHover(row.index)}
            className={`group w-[calc(100%-12px)] mx-1.5 rounded-xl px-2.5 py-2.5 flex items-center gap-3 text-left transition-colors ${
              row.index === activeIndex ? "bg-[#F26322]/10" : "hover:bg-gray-50"
            }`}
          >
            <span
              className={`grid place-items-center w-10 h-10 rounded-lg shrink-0 transition-colors ${
                row.index === activeIndex
                  ? "bg-[#F26322] text-white"
                  : "bg-[#F26322]/10 text-[#F26322]"
              }`}
            >
              <row.Icon size={20} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-[600] text-gray-800 truncate">
                {highlightMatch(row.item.title, query)}
              </span>
              {row.item.snippet && (
                <span className="block text-[13px] text-gray-400 truncate">
                  {highlightMatch(row.item.snippet, query)}
                </span>
              )}
            </span>
            <ArrowRight
              size={17}
              className={`shrink-0 text-[#F26322] transition-all ${
                row.index === activeIndex
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-1"
              }`}
            />
          </button>
        )
      )}
    </div>
  );
}
